import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import {
  type EmpresaParceiraForm,
  formatCelularMask,
  cnpjDigits,
  handleQuantidadeColaboradoresChange,
  sanitizeColaboradorNomeInput,
  segmentoSelectOptions,
} from "@/lib/empresaParceiraFields";
import InputMask from "react-input-mask";

export type EmpresaParceiraFormVariant = "edit" | "step1" | "step2" | "step3";

type Props = {
  form: EmpresaParceiraForm;
  onChange: (patch: Partial<EmpresaParceiraForm>) => void;
  idPrefix?: string;
  variant?: EmpresaParceiraFormVariant;
};

function ColaboradoresSection({
  form,
  onChange,
  idPrefix,
  title,
  strictPublicInput = false,
}: {
  form: EmpresaParceiraForm;
  onChange: (patch: Partial<EmpresaParceiraForm>) => void;
  idPrefix: string;
  title?: string;
  strictPublicInput?: boolean;
}) {
  return (
    <div className="min-w-0 max-w-full space-y-4">
      {title ? <h3 className="text-sm font-semibold text-foreground">{title}</h3> : null}
      <div className="min-w-0 max-w-full space-y-2">
        <Label htmlFor={`${idPrefix}_quantidade_colaboradores`}>
          Quantos colaboradores participarão do Clube?
        </Label>
        <Input
          id={`${idPrefix}_quantidade_colaboradores`}
          inputMode="numeric"
          maxLength={strictPublicInput ? 3 : undefined}
          value={form.quantidade_colaboradores}
          onChange={(e) =>
            onChange(
              handleQuantidadeColaboradoresChange(
                form,
                e.target.value,
                strictPublicInput ? 3 : undefined,
              ),
            )
          }
          className="w-full max-w-full border-gray-200"
        />
        {strictPublicInput ? (
          <p className="text-xs text-muted-foreground">
            Se preferir, você pode enviar posteriormente a lista dos colaboradores em uma planilha pelo
            WhatsApp.
          </p>
        ) : null}
      </div>
      {form.colaboradores_nomes.map((nome, index) => (
        <div key={`${idPrefix}_colab_${index}`} className="min-w-0 max-w-full space-y-2">
          <Label htmlFor={`${idPrefix}_colab_${index}`}>Nome do colaborador {index + 1}</Label>
          <Input
            id={`${idPrefix}_colab_${index}`}
            value={nome}
            onChange={(e) => {
              const raw = strictPublicInput ? sanitizeColaboradorNomeInput(e.target.value) : e.target.value;
              const next = [...form.colaboradores_nomes];
              next[index] = raw;
              onChange({ colaboradores_nomes: next });
            }}
            onPaste={
              strictPublicInput
                ? (e) => {
                    e.preventDefault();
                    const pasted = sanitizeColaboradorNomeInput(e.clipboardData.getData("text"));
                    const input = e.currentTarget;
                    const start = input.selectionStart ?? nome.length;
                    const end = input.selectionEnd ?? nome.length;
                    const merged = nome.slice(0, start) + pasted + nome.slice(end);
                    const next = [...form.colaboradores_nomes];
                    next[index] = sanitizeColaboradorNomeInput(merged);
                    onChange({ colaboradores_nomes: next });
                  }
                : undefined
            }
            className="w-full max-w-full border-gray-200"
          />
        </div>
      ))}
    </div>
  );
}

export function EmpresaParceiraFormFields({
  form,
  onChange,
  idPrefix = "ep",
  variant = "edit",
}: Props) {
  const showStep1 = variant === "edit" || variant === "step1";
  const showStep2 = variant === "edit" || variant === "step2";
  const showColaboradores = variant === "edit" || variant === "step3";
  const segmentoOptions = segmentoSelectOptions(form.segmento);

  return (
    <div className="min-w-0 max-w-full space-y-4">
      {showStep1 ? (
        <>
          <h3 className="text-sm font-semibold text-foreground">
            {variant === "step1" ? "1. Dados da empresa" : "Dados da empresa"}
          </h3>
          <div className="min-w-0 max-w-full space-y-2">
            <Label htmlFor={`${idPrefix}_nome_empresa`}>Nome da empresa</Label>
            <Input
              id={`${idPrefix}_nome_empresa`}
              value={form.nome_empresa}
              onChange={(e) => onChange({ nome_empresa: e.target.value })}
              className="w-full max-w-full border-gray-200"
            />
          </div>
          <div className="min-w-0 max-w-full space-y-2">
            <Label htmlFor={`${idPrefix}_cnpj`}>CNPJ</Label>
            <InputMask
              mask="99.999.999/9999-99"
              value={form.cnpj}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange({ cnpj: cnpjDigits(e.target.value).length <= 14 ? e.target.value : form.cnpj })
              }
            >
              {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
                <Input {...inputProps} id={`${idPrefix}_cnpj`} className="w-full max-w-full border-gray-200" />
              )}
            </InputMask>
          </div>
          <div className="min-w-0 max-w-full space-y-2">
            <Label htmlFor={`${idPrefix}_celular_empresa`}>Número de celular da empresa</Label>
            <Input
              id={`${idPrefix}_celular_empresa`}
              inputMode="tel"
              value={form.celular_empresa}
              onChange={(e) => onChange({ celular_empresa: formatCelularMask(e.target.value) })}
              placeholder="(00) 9 9999-9999"
              className="w-full max-w-full border-gray-200"
            />
          </div>
          <div className="min-w-0 max-w-full space-y-2">
            <Label htmlFor={`${idPrefix}_segmento`}>Segmento da empresa</Label>
            <Select
              id={`${idPrefix}_segmento`}
              value={form.segmento}
              onChange={(e) => onChange({ segmento: e.target.value })}
              placeholder="Selecione…"
              className="w-full max-w-full"
            >
              {segmentoOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-0 max-w-full space-y-2">
            <Label htmlFor={`${idPrefix}_endereco`}>Endereço da empresa</Label>
            <Input
              id={`${idPrefix}_endereco`}
              value={form.endereco}
              onChange={(e) => onChange({ endereco: e.target.value })}
              className="w-full max-w-full border-gray-200"
            />
          </div>
        </>
      ) : null}

      {showStep2 ? (
        <>
          <h3 className="text-sm font-semibold text-foreground">
            {variant === "step2" ? "2. Identificação" : "Identificação do responsável"}
          </h3>
          <div className="min-w-0 max-w-full space-y-2">
            <Label htmlFor={`${idPrefix}_responsavel`}>Nome do responsável pelo cadastro</Label>
            <Input
              id={`${idPrefix}_responsavel`}
              value={form.responsavel_nome_cargo}
              onChange={(e) => onChange({ responsavel_nome_cargo: e.target.value })}
              className="w-full max-w-full border-gray-200"
            />
          </div>
          <div className="min-w-0 max-w-full space-y-2">
            <Label htmlFor={`${idPrefix}_whatsapp`}>WhatsApp do responsável</Label>
            <Input
              id={`${idPrefix}_whatsapp`}
              inputMode="tel"
              value={form.responsavel_whatsapp}
              onChange={(e) => onChange({ responsavel_whatsapp: formatCelularMask(e.target.value) })}
              placeholder="(00) 9 9999-9999"
              className="w-full max-w-full border-gray-200"
            />
          </div>
        </>
      ) : null}

      {showColaboradores ? (
        <div className={variant === "edit" ? "border-t border-dashed border-gray-200 pt-4" : ""}>
          <ColaboradoresSection
            form={form}
            onChange={onChange}
            idPrefix={idPrefix}
            strictPublicInput={variant === "step3"}
            title={
              variant === "step3"
                ? "3. Colaboradores beneficiados"
                : variant === "edit"
                  ? "Colaboradores beneficiados"
                  : undefined
            }
          />
        </div>
      ) : null}
    </div>
  );
}
